import {
  BomOptionsEventTargetDesignLater,
  BomOptionsEventTargetPreselectBom,
  RepresentationType,
} from '../../../../bom/constants';
import { BomOptionsFormId, QuantityGroupAlertId, SubmitButtonIdOutsideBomOptionsForm } from '../../constants';
import React, { useEffect, useRef, useState } from 'react';
import {
  dispatchBomOptionsSavingCompletedEvent,
  dispatchBomOptionsSavingStartEvent,
  dispatchBomOptionsValidationPassedEvent,
} from '../../../../bom/logics';
import {
  getAllowDesignLaterForBomOptions,
  getBomOptionsConfig,
  getBomOptionsErrors,
  getBomOptionsValidationOffFlagFromParameters,
  getBomOptionsValidationSummary,
  getItemInventoryDetails,
  getQuestions,
  getWhetherCurrentContextIsBomOptionsPackage,
  getWhetherItemIsNonImprinted,
} from '../../../../bom/selectors';
import { getIsAllowDesignLater, getIsAplOrCanBranch } from '../../../../main/selectors';
import { useDispatch, useSelector } from 'react-redux';

import AlertWrapper from '../../../common/AlertWrapper';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import OrderSummary from '../OrderSummary';
import PropTypes from 'prop-types';
import QuestionList from '../QuestionList';
import SeparateQuantity from '../SeparateQuantity';
import TooltipWrapper from '../../../common/TooltipWrapper';
import { addToCart } from '../../../../main/logics';
import { getProductDetailsInterfaceSettings } from '../../../product/selectors';
import { hideProductDetailsAsync } from '../../../product/logics';
import { moveToNextFromProductDetailsAsync } from '../../../logics';
import { showWaitingCover } from '../../../../main/actions';
import styles from './BomOptions.scss';

const BomOptions = ({ onBomOptionsValidated }) => {
  const dispatch = useDispatch();

  const config = useSelector(getBomOptionsConfig);
  const errors = useSelector(getBomOptionsErrors);
  const bomOptionsValidationOff = useSelector(getBomOptionsValidationOffFlagFromParameters);
  const inventoryDetails = useSelector(getItemInventoryDetails);
  const showDesignLaterBlock = useSelector(getAllowDesignLaterForBomOptions);
  const isBomOptionsPackageContext = useSelector(getWhetherCurrentContextIsBomOptionsPackage);

  const isAplOrCan = useSelector(getIsAplOrCanBranch);
  const questions = useSelector(getQuestions);
  const validationSummary = useSelector(getBomOptionsValidationSummary);

  const settings = useSelector(getProductDetailsInterfaceSettings);
  const { showAsModal } = settings;

  const isDesignLater = useSelector(getIsAllowDesignLater);
  const isNonImprinted = useSelector(getWhetherItemIsNonImprinted);
  let addToCartButtonText = 'Add to Cart';
  if (!isDesignLater && !isNonImprinted) addToCartButtonText = 'Personalize & ' + addToCartButtonText;

  const alertRef = useRef(null);
  const [feedback, setFeedback] = useState({});

  const hasErrors = Object.keys(errors).length > 0;
  const isBomOptionsValidationOffTest = bomOptionsValidationOff && isBomOptionsPackageContext;
  const showAlert =
    Object.keys(feedback).length > 0 &&
    hasErrors &&
    questions.some((question) => question.representationType === RepresentationType.GENERAL);
  const onlyParentChildQuestions =
    questions.length > 0 &&
    questions.every((question) => question.representationType === RepresentationType.PARENT_CHILD);
  const showSubmitButtonTooltip = hasErrors && !inventoryDetails.isSoldOut;
  const submitButtonTooltipClassName = `${styles['bom-options-submit-btn-tooltip']}${
    isAplOrCan ? ` ${styles['apl-can']}` : ''
  }`;

  useEffect(() => {
    if (showAlert && !!alertRef && !!alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
    }
  }, [showAlert]);

  const resetFeedback = (field) => {
    if (!!feedback[field]) {
      setFeedback({ ...feedback, [field]: null });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isBomOptionsValidationOffTest) {
      if (Object.keys(errors).length > 0) {
        setFeedback(errors);

        if (showAlert && !!alertRef && !!alertRef.current) {
          alertRef.current.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
        }

        if (onlyParentChildQuestions) {
          const quantityGroupAlert = document.getElementById(QuantityGroupAlertId);
          if (!!quantityGroupAlert) {
            quantityGroupAlert.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
          }
        }

        return;
      }
    }

    const submitterId = e.nativeEvent.submitter.id;
    if (submitterId === 'btn-add-to-cart') {
      if (isBomOptionsPackageContext) {
        dispatchBomOptionsSavingStartEvent(BomOptionsEventTargetPreselectBom);

        dispatch(showWaitingCover());

        dispatch(
          addToCart({
            preselectBomOptions: true,
            onSaveBomOptionsSucceeded: (response) => {
              const cartItemId =
                !!response && !!response.data && !!response.data.cartIds && response.data.cartIds.length > 0
                  ? response.data.cartIds[0]
                  : 0;
              dispatchBomOptionsSavingCompletedEvent({
                cartItemId,
                target: BomOptionsEventTargetPreselectBom,
              });
            },
          })
        );
      } else {
        dispatch(hideProductDetailsAsync({ acceptBomOptions: true }));
        dispatch(moveToNextFromProductDetailsAsync());
      }
    } else if (submitterId === 'btn-design-later') {
      dispatchBomOptionsValidationPassedEvent(BomOptionsEventTargetDesignLater);
    } else if (submitterId === SubmitButtonIdOutsideBomOptionsForm) {
      if (!!onBomOptionsValidated && typeof onBomOptionsValidated === 'function') {
        onBomOptionsValidated();
      } else {
        dispatch(hideProductDetailsAsync({ acceptBomOptions: true }));
        dispatch(moveToNextFromProductDetailsAsync());
      }
    }
  };

  return (
    <div className={styles['bom-options']}>
      <Form className={styles['bom-options-form']} id={BomOptionsFormId} noValidate onSubmit={handleSubmit}>
        {showAlert && (
          <AlertWrapper ref={alertRef} variant="danger">
            {validationSummary.alert}
          </AlertWrapper>
        )}
        <div className={styles['form-groups-holder']}>
          {config.showQuestionList && <QuestionList feedback={feedback} resetFeedback={resetFeedback} />}
          {config.showSeparateQuantity && <SeparateQuantity feedback={feedback} resetFeedback={resetFeedback} />}
        </div>
        {!showAsModal && (
          <>
            <hr />
            <OrderSummary />
            <div className={styles['submit-buttons-holder']}>
              {!isBomOptionsValidationOffTest && showSubmitButtonTooltip ? (
                <TooltipWrapper
                  className={submitButtonTooltipClassName}
                  id="btn-add-to-cart-tooltip"
                  message={validationSummary.tooltip}
                  trigger={['focus', 'hover']}>
                  <Button
                    aria-disabled="true"
                    className={`${styles['btn-add-to-cart']} ${styles['not-allowed']}`}
                    id="btn-add-to-cart"
                    type="submit"
                    variant="success">
                    {addToCartButtonText}
                  </Button>
                </TooltipWrapper>
              ) : (
                <Button
                  className={styles['btn-add-to-cart']}
                  disabled={inventoryDetails.isSoldOut}
                  id="btn-add-to-cart"
                  type="submit"
                  variant="success">
                  {addToCartButtonText}
                </Button>
              )}
              {showDesignLaterBlock && (
                <>
                  <small>Use our easy design studio</small>
                  <span>or</span>
                  {showSubmitButtonTooltip ? (
                    <TooltipWrapper
                      className={submitButtonTooltipClassName}
                      id="btn-design-later-tooltip"
                      message={validationSummary.tooltip}
                      trigger={['focus', 'hover']}>
                      {isBomOptionsValidationOffTest ? (
                        <div className={styles['design-later-button-wrapper']}>
                          <Button
                            aria-disabled="true"
                            className={`${styles['btn-design-later']} ${styles['not-allowed']}`}
                            disabled={true}
                            id="btn-design-later"
                            type="submit"
                            variant="outline-primary">
                            Design It For Me
                          </Button>
                        </div>
                      ) : (
                        <Button
                          aria-disabled="true"
                          className={`${styles['btn-design-later']} ${styles['not-allowed']}`}
                          id="btn-design-later"
                          type="submit"
                          variant="outline-primary">
                          Design It For Me
                        </Button>
                      )}
                    </TooltipWrapper>
                  ) : (
                    <Button
                      className={styles['btn-design-later']}
                      disabled={inventoryDetails.isSoldOut}
                      id="btn-design-later"
                      type="submit"
                      variant="outline-primary">
                      Design It For Me
                    </Button>
                  )}
                  <small>Select your options & our designers will put it together for FREE</small>
                </>
              )}
            </div>
          </>
        )}
      </Form>
    </div>
  );
};

BomOptions.propTypes = {
  onBomOptionsValidated: PropTypes.func,
};

export default BomOptions;
